import type { ReactNode } from "react";
import { matchPath, Navigate, Outlet, useLocation } from "react-router";
import { Page } from "@/components/Page";
import { type Crumb, PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { useViewport } from "@/responsive/viewport";
import { ListNav, type ListNavGroup, type ListNavItem, visibleGroups } from "./ListNav";

export interface ListNavLayoutProps {
  /** Area root, e.g. "/admin". */
  basePath: string;
  title: string;
  subtitle?: string;
  /** Breadcrumb ancestors above the area (e.g. the space, for space settings). */
  trail?: Crumb[];
  groups: ListNavGroup[];
  /** Where the bare `basePath` goes on desktop/tablet. Defaults to the first visible item. */
  defaultTo?: string;
  mobileIntro?: ReactNode;
  "aria-label": string;
  /** Passed to every section via `useOutletContext` (e.g. the entity being edited). */
  outletContext?: unknown;
}

function trimSlash(path: string): string {
  let end = path.length;
  while (end > 1 && path[end - 1] === "/") end--;
  return path.slice(0, end);
}

/** The item whose `to` is the longest prefix of the current path. */
function activeItem(groups: ListNavGroup[], pathname: string): ListNavItem | undefined {
  let best: ListNavItem | undefined;
  for (const item of groups.flatMap((g) => g.items)) {
    if (matchPath({ path: item.to, end: false }, pathname) && item.to.length > (best?.to.length ?? -1)) best = item;
  }
  return best;
}

/**
 * List → detail frame shared by Admin and Settings. Every section is a real
 * child route rendered in the `<Outlet />`.
 *
 * - Desktop/tablet: nav column + section; the bare root redirects to `defaultTo`.
 * - Mobile: the bare root *is* the grouped list.
 *
 * A section's own page gets the breadcrumb `…trail / {title} / {section}` when there is a
 * trail (a space's settings). Deeper pages (`/admin/users/new`) render their own PageBreadcrumbs.
 */
export function ListNavLayout({
  basePath,
  title,
  subtitle,
  groups,
  trail = [],
  defaultTo,
  mobileIntro,
  "aria-label": ariaLabel,
  outletContext,
}: Readonly<ListNavLayoutProps>) {
  const { isMobile } = useViewport();
  const { pathname } = useLocation();
  const shown = visibleGroups(groups);
  const isRoot = trimSlash(pathname) === trimSlash(basePath);
  const current = activeItem(shown, pathname);
  const isSectionRoot = current !== undefined && trimSlash(pathname) === trimSlash(current.to);
  const breadcrumbs = isSectionRoot && trail.length > 0 && (
    <PageBreadcrumbs trail={[...trail, { label: title, to: basePath }]} current={current.label} />
  );

  if (isMobile) {
    if (isRoot) {
      return (
        <>
          {trail.length > 0 && <PageBreadcrumbs trail={trail} current={title} />}
          <Page title={title} subtitle={subtitle}>
            {mobileIntro}
            <ListNav groups={shown} aria-label={ariaLabel} variant="list" />
          </Page>
        </>
      );
    }
    return (
      <div className="flex min-h-full flex-col">
        {breadcrumbs}
        <Outlet context={outletContext} />
      </div>
    );
  }

  if (isRoot) {
    const target = defaultTo ?? shown[0]?.items[0]?.to;
    if (target && trimSlash(target) !== trimSlash(basePath)) return <Navigate to={target} replace />;
  }

  return (
    <div className="flex min-h-full">
      <aside className="w-56 shrink-0 border-r border-border-subtle">
        <div className="sticky top-0 max-h-dvh overflow-y-auto px-3 py-8">
          <ListNav groups={shown} aria-label={ariaLabel} />
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        {breadcrumbs}
        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
